import type {
  ApiErrorBody,
  ArticleCard,
  ArticleDetail,
  AuthorProfile,
  AuthorRef,
  BreakingItem,
  CategoryRef,
  DistrictRef,
  HomepagePayload,
  LiveEventDetail,
  LiveEventSummary,
  Locale,
  SearchHit,
  SearchSort,
  TagRef,
  VideoCard,
  VideoDetail,
} from '@nk/shared';

/**
 * The typed API client — phases.md Phase 7 ("a typed `apiClient` wrapper, and shared types
 * imported from `packages/shared`").
 *
 * One function per public endpoint, each returning the Phase 6 payload type rather than
 * `unknown`. No page constructs a URL or knows the envelope shape: `{ data, meta }` is
 * unwrapped here, once, so a change to §2's envelope is a change to this file.
 *
 * Every request is same-origin (ADR-001), so there is no base URL to configure and no
 * CORS to think about.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields?: Record<string, string>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.error.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.error.code;
    this.fields = body.error.fields;
  }
}

const FALLBACK_ERROR_BODY: ApiErrorBody = {
  error: { code: 'UNKNOWN', message: 'Something went wrong' },
};

interface Envelope<T> {
  data: T;
  meta?: Record<string, unknown>;
}

type QueryValue = string | number | boolean | undefined | null;

function buildUrl(path: string, query: Record<string, QueryValue> = {}): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const search = params.toString();
  return `/api/v1${path}${search ? `?${search}` : ''}`;
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  // A FormData body (file uploads) must not get a manual Content-Type — the browser sets
  // its own multipart boundary, and overriding it breaks Multer's parsing on the API side.
  const isFormData = init.body instanceof FormData;

  const res = await fetch(path.startsWith('/api/') ? path : `/api/v1${path}`, {
    ...init,
    // Same-origin, but explicit: the browser must send the HttpOnly session cookie.
    credentials: 'include',
    headers: isFormData ? init.headers : { 'Content-Type': 'application/json', ...init.headers },
  });

  const body: unknown = await res.json().catch(() => undefined);

  if (!res.ok) {
    throw new ApiError(res.status, isApiErrorBody(body) ? body : FALLBACK_ERROR_BODY);
  }
  return (body as Envelope<T>).data;
}

/**
 * Keeps `meta` — the cursor feeds need `nextCursor`, search needs `total`, and the CMS's
 * offset tables need `page`/`perPage`/`total` (Phase 14).
 */
export async function apiFetchEnvelope<T>(
  path: string,
  init: RequestInit = {},
): Promise<Envelope<T>> {
  const isFormData = init.body instanceof FormData;
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: isFormData ? init.headers : { 'Content-Type': 'application/json', ...init.headers },
  });
  const body: unknown = await res.json().catch(() => undefined);
  if (!res.ok) {
    throw new ApiError(res.status, isApiErrorBody(body) ? body : FALLBACK_ERROR_BODY);
  }
  return body as Envelope<T>;
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  return typeof value === 'object' && value !== null && 'error' in value;
}

export interface CursorResult<T> {
  items: T[];
  nextCursor: string | null;
}

export interface FeedParams {
  locale: Locale;
  category?: string;
  district?: string;
  tag?: string;
  author?: string;
  cursor?: string;
  limit?: number;
}

export interface SearchParams {
  locale: Locale;
  q: string;
  category?: string;
  district?: string;
  author?: string;
  /** Date range, inclusive, as `YYYY-MM-DD` — the value an `<input type="date">` holds. */
  from?: string;
  to?: string;
  sort?: SearchSort;
  page?: number;
  limit?: number;
}

export interface SearchResult {
  hits: SearchHit[];
  total: number;
  page: number;
  perPage: number;
}

export const publicApi = {
  homepage: (locale: Locale): Promise<HomepagePayload> =>
    apiFetch<HomepagePayload>(buildUrl('/public/homepage', { locale })),

  articles: async (params: FeedParams): Promise<CursorResult<ArticleCard>> => {
    const envelope = await apiFetchEnvelope<ArticleCard[]>(
      buildUrl('/public/articles', { ...params }),
    );
    return { items: envelope.data, nextCursor: (envelope.meta?.['nextCursor'] as string) ?? null };
  },

  article: (
    locale: Locale,
    segments: { category: string; district?: string; slug: string },
  ): Promise<ArticleDetail> =>
    apiFetch<ArticleDetail>(
      buildUrl(
        `/public/articles/${[segments.category, segments.district, segments.slug]
          .filter(Boolean)
          .join('/')}`,
        { locale },
      ),
    ),

  categories: (locale: Locale): Promise<CategoryRef[]> =>
    apiFetch<CategoryRef[]>(buildUrl('/public/categories', { locale })),

  districts: (locale: Locale): Promise<DistrictRef[]> =>
    apiFetch<DistrictRef[]>(buildUrl('/public/districts', { locale })),

  /** Bylines with published work, for search's author filter. */
  authors: (locale: Locale): Promise<AuthorRef[]> =>
    apiFetch<AuthorRef[]>(buildUrl('/public/authors', { locale })),

  tag: (
    locale: Locale,
    slug: string,
    cursor?: string,
  ): Promise<{ tag: TagRef; articles: ArticleCard[]; nextCursor: string | null }> =>
    apiFetchEnvelope<{ tag: TagRef; articles: ArticleCard[] }>(
      buildUrl(`/public/tags/${slug}`, { locale, cursor }),
    ).then((envelope) => ({
      ...envelope.data,
      nextCursor: (envelope.meta?.['nextCursor'] as string) ?? null,
    })),

  author: (
    locale: Locale,
    slug: string,
    cursor?: string,
  ): Promise<{ author: AuthorProfile; articles: ArticleCard[]; nextCursor: string | null }> =>
    apiFetchEnvelope<{ author: AuthorProfile; articles: ArticleCard[] }>(
      buildUrl(`/public/authors/${slug}`, { locale, cursor }),
    ).then((envelope) => ({
      ...envelope.data,
      nextCursor: (envelope.meta?.['nextCursor'] as string) ?? null,
    })),

  search: async (params: SearchParams): Promise<SearchResult> => {
    const envelope = await apiFetchEnvelope<SearchHit[]>(buildUrl('/public/search', { ...params }));
    return {
      hits: envelope.data,
      total: Number(envelope.meta?.['total'] ?? 0),
      page: Number(envelope.meta?.['page'] ?? 1),
      perPage: Number(envelope.meta?.['perPage'] ?? 20),
    };
  },

  trending: (locale: Locale, limit = 10): Promise<ArticleCard[]> =>
    apiFetch<ArticleCard[]>(buildUrl('/public/trending', { locale, limit })),

  breaking: (locale: Locale): Promise<BreakingItem[]> =>
    apiFetch<BreakingItem[]>(buildUrl('/public/breaking', { locale })),

  liveEvents: (locale: Locale): Promise<LiveEventSummary[]> =>
    apiFetch<LiveEventSummary[]>(buildUrl('/public/live-events', { locale })),

  liveEvent: (locale: Locale, slug: string, page = 1): Promise<LiveEventDetail> =>
    apiFetch<LiveEventDetail>(buildUrl(`/public/live-events/${slug}`, { locale, page })),

  videos: async (params: {
    locale: Locale;
    category?: string;
    cursor?: string;
    limit?: number;
  }): Promise<CursorResult<VideoCard>> => {
    const envelope = await apiFetchEnvelope<VideoCard[]>(buildUrl('/public/videos', { ...params }));
    return { items: envelope.data, nextCursor: (envelope.meta?.['nextCursor'] as string) ?? null };
  },

  video: (locale: Locale, slug: string): Promise<VideoDetail> =>
    apiFetch<VideoDetail>(buildUrl(`/public/videos/${slug}`, { locale })),
};
